import * as React from 'react'
import * as z from 'zod'
import { useLingui } from '@lingui/react/macro'
import { useAppStore } from './app-store'
import type { Locale } from 'shared/types'

const zodLocaleMap: Record<Locale, () => Promise<{ localeError: z.core.$ZodErrorMap }>> = {
  'es-ES': () => import('zod/v4/locales/es.js').then((m) => m.default()),
  'ca-ES': () => import('zod/v4/locales/ca.js').then((m) => m.default()),
  'en-US': () => import('zod/v4/locales/en.js').then((m) => m.default()),
  'pt-BR': () => import('zod/v4/locales/pt.js').then((m) => m.default()),
}

export function useZodI18n() {
  const { t } = useLingui()
  const locale = useAppStore((state) => state.store.locale)

  React.useEffect(() => {
    zodLocaleMap[locale]().then((zodLocale) => {
      z.config({
        ...zodLocale,
        customError: (issue) => {
          switch (issue.code) {
            case 'too_small': {
              if ((issue.origin === 'string' || issue.origin === 'array') && issue.minimum === 1) return t`Required`
              const min = String(issue.minimum)
              if (issue.origin === 'string') return t`Minimum ${min} characters`
              if (issue.origin === 'number' || issue.origin === 'int') return t`Minimum ${min}`
              return undefined
            }
            case 'too_big': {
              const max = String(issue.maximum)
              if (issue.origin === 'string') return t`Maximum ${max} characters`
              if (issue.origin === 'number' || issue.origin === 'int') return t`Maximum ${max}`
              return undefined
            }
            case 'invalid_type': {
              // In a form context an unexpected type almost always means the field was left
              // empty/unset (string, number, boolean, enum, date…), so surface "Required"
              // rather than Zod's default "Entrada inválida".
              return t`Required`
            }
            case 'invalid_value': {
              // A `z.literal(true)` is a consent checkbox (only the terms-of-use fields use it).
              const values = (issue as { values?: unknown[] }).values
              if (values?.length === 1 && values[0] === true) return t`You must accept the terms of use`
              if (!issue.input) return t`Required`
              return undefined
            }
            case 'custom': {
              // Conditionally-required fields (see jobInfoSchema) push a message-less custom issue
              // tagged with this marker so the wording lives here rather than in the schema.
              if ((issue as { params?: { i18n?: string } }).params?.i18n === 'required') return t`Required`
              return undefined
            }
            case 'invalid_format': {
              if (issue.format === 'email') return t`Invalid email`
              if (issue.format === 'url') return t`Invalid URL`
              if (issue.format === 'date' || issue.format === 'datetime') return t`Invalid date`
              return undefined
            }
            default:
              return undefined
          }
        },
      })
    })
  }, [t, locale])
}
